import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Instagram, Youtube, MapPin, IndianRupee, Award, ChevronLeft, BadgeCheck, TrendingUp, Sparkles, Briefcase, FileText } from 'lucide-react';
import { FileUpload } from '../components/FileUpload';
import { FileList } from '../components/FileList';
import { useAuth } from '../context/AuthContext';
import { ApifyRefreshButton, APIFY_REFRESH_ROLES } from '../components/ApifyRefreshButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : n);

const stageColors = {
  pitched: '#0055FF', outreach: '#9B6EFF', locked: '#00C972', live: '#FF451A', completed: '#666666', declined: '#FF3B30',
};

export const CreatorProfile = () => {
  const { id } = useParams();
  const { user: viewer } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filesRefresh, setFilesRefresh] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/creators/${id}/profile`, { withCredentials: true });
        setData(data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, reloadKey]);

  if (loading) {
    return <div className="flex"><Sidebar /><div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]"><div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div></div></div>;
  }

  if (!data) {
    return (
      <div className="flex min-h-screen"><Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
            <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Creator not found</h3>
            <Link to="/admin/roster" className="text-[#FF451A] font-medium">← Back to roster</Link>
          </div>
        </div>
      </div>
    );
  }

  const c = data.creator;
  const stats = data.stats || {};
  const isExcl = c.exclusivity_type === 'exclusive';

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 bg-white dark:bg-[#0A0B0D]" data-testid="creator-profile-page">
        <div className="max-w-6xl mx-auto p-8">
          <Link to="/admin/roster" className="inline-flex items-center gap-1 text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] mb-4">
            <ChevronLeft size={14} /> Back
          </Link>

          {/* Hero card */}
          <div className="bg-gradient-to-br from-[#111111] to-[#1a1a1a] rounded-2xl p-8 text-white relative overflow-hidden mb-6">
            <div className="absolute -right-10 -top-10 w-60 h-60 rounded-full bg-[#FF451A]/30 blur-3xl"></div>
            <div className="relative z-10 flex items-start gap-6 flex-wrap">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-3xl font-bold shrink-0 text-white">
                {c.profile_photo_url ? <img src={c.profile_photo_url} alt={c.name} className="w-full h-full rounded-2xl object-cover" /> : c.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-3xl font-bold tracking-tight text-white">{c.name}</h1>
                  {isExcl && <span className="px-2 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold flex items-center gap-1 border border-white/15"><Award size={11} /> Managed</span>}
                  <span className="px-2 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold uppercase tracking-wider border border-white/15">Grade {c.quality_grade}</span>
                  <BadgeCheck size={18} className="text-[#0055FF]" />
                </div>
                {c.instagram_handle && <p className="text-sm text-white/80 flex items-center gap-1"><Instagram size={12} /> @{c.instagram_handle}</p>}
                {c.bio && <p className="text-sm text-white/80 mt-2 max-w-xl">{c.bio}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(c.categories || []).map((cat) => (
                    <span key={cat} className="px-2 py-0.5 rounded-full bg-white/10 text-white/90 text-xs border border-white/15">{cat}</span>
                  ))}
                </div>
              </div>
              {c.instagram_handle && APIFY_REFRESH_ROLES.includes(viewer?.role) && (
                <div className="flex flex-col items-end gap-1">
                  <ApifyRefreshButton
                    creatorIds={[c.id || id]}
                    label="Refresh Data"
                    onDone={() => setReloadKey((k) => k + 1)}
                    className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    testid="creator-refresh-data-btn"
                  />
                  {c.apify_last_refreshed && (
                    <span className="text-[10px] text-white/60" data-testid="creator-last-refreshed">
                      Refreshed {new Date(c.apify_last_refreshed).toLocaleDateString()}{c.apify_status === 'not_found' ? ' · profile not found' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 relative z-10">
              <HeroStat label="IG Followers" value={fmt(c.instagram_followers || 0)} icon={Instagram} />
              <HeroStat label="YT Subscribers" value={fmt(c.youtube_subscribers || 0)} icon={Youtube} />
              <HeroStat label="Engagement" value={`${(c.instagram_engagement || 0).toFixed(1)}%`} icon={TrendingUp} />
              <HeroStat label="Reel Price" value={`₹${fmt(c.pricing_reel || 0)}`} icon={IndianRupee} />
            </div>
          </div>

          {/* Performance stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <KCard label="Total Campaigns" value={stats.total_campaigns || 0} />
            <KCard label="Locked / Live" value={stats.locked_or_completed || 0} accent="#00C972" />
            <KCard label="Declined" value={stats.declined || 0} accent="#FF3B30" />
            <KCard label="Acceptance Rate" value={`${stats.acceptance_rate || 0}%`} accent="#FF451A" />
            <KCard label="Earnings" value={`₹${(stats.total_earnings || 0).toLocaleString()}`} accent="#0055FF" />
            <KCard label="Brands" value={stats.brands_count || 0} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Media Kit + AI insights + brands */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#FF451A]/10 to-[#0F0F0F] border border-[#FF451A]/30 rounded-2xl p-5 shadow-[0_0_30px_rgba(255,69,26,0.1)]" data-testid="media-kit-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-2"><FileText size={16} className="text-[#FF451A]" /> Media Kit & Files</h3>
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#FF451A]">Supabase</span>
                </div>
                {(viewer?.id === id || viewer?.role === 'admin') && (
                  <div className="mb-3">
                    <FileUpload
                      bucket="creator-media-kits"
                      attachedTo="creator"
                      attachedId={id}
                      accept="application/pdf,image/*"
                      maxBytes={10 * 1024 * 1024}
                      label="Upload media kit (PDF/image, max 10MB)"
                      onUploaded={() => setFilesRefresh((n) => n + 1)}
                    />
                  </div>
                )}
                <FileList
                  attachedTo="creator"
                  attachedId={id}
                  refreshKey={filesRefresh}
                  canDelete={viewer?.id === id || viewer?.role === 'admin'}
                  onDeleted={() => setFilesRefresh((n) => n + 1)}
                />
              </div>

              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
                <h3 className="font-semibold text-[#0F0F0F] dark:text-white mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#FF451A]" /> AI Suitability</h3>
                {data.suitability_tags?.length > 0 ? (
                  <ul className="space-y-2">
                    {data.suitability_tags.map((t, i) => (
                      <li key={i} className="text-sm text-[#0F0F0F] dark:text-white flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF451A] mt-1.5 shrink-0"></span>
                        {t}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[#6B6B6B] dark:text-zinc-500">No suitability tags yet — needs more campaign history.</p>
                )}
              </div>

              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
                <h3 className="font-semibold text-[#0F0F0F] dark:text-white mb-3">Brands worked with</h3>
                {(data.brands_worked_with || []).length === 0 ? (
                  <p className="text-sm text-[#6B6B6B] dark:text-zinc-500">No completed campaigns yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {data.brands_worked_with.map((b) => (
                      <span key={b} className="px-3 py-1 bg-zinc-100 dark:bg-[#131518] text-[#0F0F0F] dark:text-white rounded-full text-xs font-medium border border-black/[0.08] dark:border-white/5">{b}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: campaign history */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl">
                <div className="p-5 border-b border-black/[0.08] dark:border-white/5">
                  <h3 className="font-semibold text-[#0F0F0F] dark:text-white flex items-center gap-2"><Briefcase size={16} /> Campaign History</h3>
                </div>
                {(data.history || []).length === 0 ? (
                  <p className="p-8 text-sm text-[#6B6B6B] dark:text-zinc-500 text-center">No campaigns yet.</p>
                ) : (
                  <div className="divide-y divide-black/5">
                    {data.history.map((h) => (
                      <Link
                        key={h.id}
                        to={`/campaigns/${h.id}/workspace`}
                        className="block p-4 hover:bg-white/[0.03] transition-colors"
                        data-testid="creator-history-row"
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-[#0F0F0F] dark:text-white truncate">{h.name}</div>
                            <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">{h.brand}</div>
                          </div>
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                            style={{ backgroundColor: `${stageColors[h.stage] || '#999'}15`, color: stageColors[h.stage] || '#999' }}
                          >
                            {h.stage}
                          </span>
                          <span className="text-xs font-semibold text-[#0F0F0F] dark:text-white">₹{(h.budget || 0).toLocaleString()}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroStat = ({ label, value, icon: Icon }) => (
  <div className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/10">
    <div className="text-[10px] uppercase tracking-wider text-white/70 mb-1 flex items-center gap-1"><Icon size={10} /> {label}</div>
    <div className="text-xl font-bold text-white">{value}</div>
  </div>
);

const KCard = ({ label, value, accent }) => (
  <div className="bg-white dark:bg-[#131518] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4">
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1">{label}</div>
    <div className="text-2xl font-bold text-[#0F0F0F] dark:text-white" style={accent ? { color: accent } : undefined}>{value}</div>
  </div>
);
